import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link as LinkIcon,
} from 'lucide-react';
import { ThemeColors, Spacing, Typography, BorderRadius } from '../../styles/ThemeStyles';
import { cleanEditorHtml, normalizeUrl, stripHtml } from '../../utils/formatText';

const EditorWrapper = styled.div<{ $disabled?: boolean; $hasError?: boolean }>`
  border: 2px solid
    ${(props) =>
      props.$hasError ? ThemeColors.error.main : ThemeColors.border.main};
  border-radius: ${BorderRadius.md};
  overflow: hidden;
  background: white;
  transition: all 0.2s ease;
  opacity: ${(props) => (props.$disabled ? 0.7 : 1)};

  &:focus-within {
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light};
  }
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${Spacing.xs};
  padding: ${Spacing.sm};
  border-bottom: 1px solid ${ThemeColors.border.light};
  background: ${ThemeColors.background.cards};
`;

const ToolbarButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid
    ${(props) =>
      props.$active ? ThemeColors.primary.main : ThemeColors.border.light};
  border-radius: ${BorderRadius.sm};
  background: ${(props) =>
    props.$active ? ThemeColors.primary.light : 'white'};
  color: ${(props) =>
    props.$active ? ThemeColors.primary.dark : ThemeColors.text.secondary};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${ThemeColors.primary.main};
    color: ${ThemeColors.primary.main};
    background: ${ThemeColors.primary.light};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const EditableArea = styled.div`
  min-height: 120px;
  max-height: 280px;
  overflow-y: auto;
  padding: ${Spacing.md};
  font-size: ${Typography.fontSize.md};
  font-family: inherit;
  line-height: 1.6;
  color: ${ThemeColors.text.primary};
  outline: none;

  &:empty::before {
    content: attr(data-placeholder);
    color: ${ThemeColors.text.secondary};
    pointer-events: none;
  }

  ul,
  ol {
    margin: 0.5em 0;
    padding-left: 1.5em;
  }

  a {
    color: ${ThemeColors.primary.main};
    text-decoration: underline;
    word-break: normal;
    overflow-wrap: normal;
  }
`;

const Hint = styled.p`
  margin: ${Spacing.xs} 0 0;
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
`;

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Enter description...',
  disabled = false,
  hasError = false,
}) => {
  const editorRef = useRef<HTMLDivElement>(null);
  const [activeFormats, setActiveFormats] = useState<Record<string, boolean>>({});

  const syncValueFromEditor = useCallback(() => {
    const html = cleanEditorHtml(editorRef.current?.innerHTML || '');
    onChange(html);
  }, [onChange]);

  const updateActiveFormats = useCallback(() => {
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      unorderedList: document.queryCommandState('insertUnorderedList'),
      orderedList: document.queryCommandState('insertOrderedList'),
    });
  }, []);

  const execFormat = (command: string, commandValue?: string) => {
    if (disabled) return;
    editorRef.current?.focus();
    document.execCommand(command, false, commandValue);
    syncValueFromEditor();
    updateActiveFormats();
  };

  const handleLink = () => {
    if (disabled) return;

    const selection = window.getSelection();
    const selectedText = selection?.toString().trim();
    const defaultUrl = selectedText && /^(https?:\/\/|www\.)/i.test(selectedText)
      ? normalizeUrl(selectedText)
      : 'https://';

    const url = window.prompt('Enter link URL', defaultUrl);
    if (!url?.trim()) return;

    const href = normalizeUrl(url);
    if (!/^https?:\/\//i.test(href)) {
      window.alert('Please enter a valid URL (starting with http:// or https://).');
      return;
    }

    editorRef.current?.focus();
    document.execCommand('createLink', false, href);
    syncValueFromEditor();
    updateActiveFormats();
  };

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    const current = editor.innerHTML;
    const normalizedValue = value || '';
    if (stripHtml(current) === stripHtml(normalizedValue) && current === normalizedValue) {
      return;
    }

    if (current !== normalizedValue) {
      editor.innerHTML = normalizedValue;
    }
  }, [value]);

  return (
    <div>
      <EditorWrapper $disabled={disabled} $hasError={hasError}>
        <Toolbar>
          <ToolbarButton
            type="button"
            title="Bold"
            $active={activeFormats.bold}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execFormat('bold')}
          >
            <Bold />
          </ToolbarButton>
          <ToolbarButton
            type="button"
            title="Italic"
            $active={activeFormats.italic}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execFormat('italic')}
          >
            <Italic />
          </ToolbarButton>
          <ToolbarButton
            type="button"
            title="Underline"
            $active={activeFormats.underline}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execFormat('underline')}
          >
            <Underline />
          </ToolbarButton>
          <ToolbarButton
            type="button"
            title="Bullet list"
            $active={activeFormats.unorderedList}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execFormat('insertUnorderedList')}
          >
            <List />
          </ToolbarButton>
          <ToolbarButton
            type="button"
            title="Numbered list"
            $active={activeFormats.orderedList}
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => execFormat('insertOrderedList')}
          >
            <ListOrdered />
          </ToolbarButton>
          <ToolbarButton
            type="button"
            title="Insert link"
            disabled={disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleLink}
          >
            <LinkIcon />
          </ToolbarButton>
        </Toolbar>
        <EditableArea
          ref={editorRef}
          contentEditable={!disabled}
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onInput={syncValueFromEditor}
          onBlur={syncValueFromEditor}
          onKeyUp={updateActiveFormats}
          onMouseUp={updateActiveFormats}
        />
      </EditorWrapper>
      <Hint>Paste or type URLs and they will become clickable links on the resource page.</Hint>
    </div>
  );
};
